import { RocheUIGrid } from '@repo/roche-ui';
import { StringService as StringHelper } from '@rocheglobal/component-library-dot-com/src/services/string/index';
import { TranslationService } from '@rocheglobal/component-library-dot-com/src/services/translation';
import React, { useEffect, useState } from 'react';
import { ExternalDataService } from '../../services/external-data';
import { Continent, Location, RocheEndpoint } from '../../services/external-data/types';
import { LocationService } from '../../services/location';
import { ContinentInfo } from '../../services/location/types';

interface ListProps {
  continent: Continent;
}

const getTranslations = async (): Promise<Record<string, unknown>> => {
  const translations = {
    translations: {
      viewOnGoogleMaps: await TranslationService.translate('view on google maps'),
      all: await TranslationService.translate('all'),
      offices: await TranslationService.translate('offices'),
      office: await TranslationService.translate('office'),
    },
  };
  return translations;
};

export const RocheWorldwideLocationOverview = (): JSX.Element => {
  const [state, setState] = useState<Array<ContinentInfo>>(LocationService.continents);

  useEffect(() => {
    const getTranslatedContinents = async () => {
      const translatedContinents = await Promise.all(
        LocationService.continents.map(async (continent) => ({
          ...continent,
          displayName: await TranslationService.translate(continent.displayName),
        })),
      );

      setState(translatedContinents);
    };

    getTranslatedContinents();
  }, []);

  return (
    <RocheUIGrid layout='12'>
      <roche-accordion-tabs type='tabs' appearance='filter' start-position={1} slot='full'>
        {state.map((continentInfo) => (
          <roche-accordion-tabs-item
            headline={StringHelper.capitalize(continentInfo.displayName)}
            key={continentInfo.continent}
            id={continentInfo.displayName}
            update-on-content-change
          >
            <List continent={continentInfo.continent}></List>
          </roche-accordion-tabs-item>
        ))}
      </roche-accordion-tabs>
    </RocheUIGrid>
  );
};

const List = (props: ListProps): JSX.Element => {
  const [state, setState] = useState({
    items: [],
    page: 1,
    total: 10,
    translations: {
      viewOnGoogleMaps: '',
      all: '',
      offices: '',
      office: '',
    },
  });

  const { continent } = props;

  useEffect(() => {
    ExternalDataService.getAllCountryLocationsByContinent({
      continent,
      pageSize: state.total,
    }).then(async (result: RocheEndpoint<Location[]>) => {
      ExternalDataService.handle404(result);

      const { Items, Total, Page } = result;

      return setState({
        ...state,
        items: Items,
        total: Total,
        page: Page,
        ...(await getTranslations()),
      });
    });
  }, [state.total]);

  if (!state) {
    return null;
  }

  const addresses = state.items || [];

  return (
    <>
      {addresses.map((item, index) => {
        const addressList = Object.keys(item)
          .filter((addressString) => addressString.includes('address_line'))
          .map((address) => `<br/>${item[address]}`)
          .join('');

        const addressListWithCountry = `${addressList} </br/> ${item.country}`;

        return (
          <div key={index}>
            <roche-headline level={3}>
              <h3 dangerouslySetInnerHTML={{ __html: item.address_line_1 }}></h3>
            </roche-headline>
            <roche-richtext
              dangerouslySetInnerHTML={{
                __html: addressListWithCountry,
              }}
            />
            <div>
              {item.latitude && item.longitude && (
                <roche-text-link
                  href={`http://www.google.com/maps/place/${item.latitude},${item.longitude}`}
                  text={StringHelper.capitalize(state.translations?.viewOnGoogleMaps)}
                  target='_blank'
                  right-icon='external-link'
                ></roche-text-link>
              )}
            </div>
            <div>
              {item.telephone_number && (
                <roche-text-link href={`tel:${item.telephone_number}`} text={item.telephone_number} left-icon='mobile'>
                  {' '}
                </roche-text-link>
              )}
            </div>
            <hr />
          </div>
        );
      })}
    </>
  );
};
